'use client'

import React, { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Calendar, Check, X, Clock, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAbsences } from '@/hooks/queries'
import { useQueryClient } from '@tanstack/react-query'

interface Absence {
  id: string
  absenceType: string
  startDate: string
  endDate: string
  totalDays: number
  reason?: string
  status: string
  staff?: {
    fullName: string
  }
  approvedBy?: {
    fullName: string
  }
  approvedAt?: string
}

interface AbsencesListProps {
  staffId: string
  clinicId: string
  showAll?: boolean // If true, show all staff absences (for managers)
  onApprove?: (id: string) => void
  onReject?: (id: string) => void
}

export default function AbsencesList({
  staffId,
  clinicId,
  showAll = false,
  onApprove,
  onReject
}: AbsencesListProps) {
  const { t } = useTranslation('hr')
  const [filter, setFilter] = useState<string>('all')
  const queryClient = useQueryClient()
  
  // Use React Query for caching - 30 minute cache
  const { 
    data: queryData, 
    isLoading: loading,
    refetch,
    isFetching 
  } = useAbsences(clinicId)
  
  // Filter absences based on showAll prop
  const absences = useMemo(() => {
    if (!queryData?.absences) return []
    if (showAll) return queryData.absences
    return queryData.absences.filter((a: any) => a.user_id === staffId || a.staff_id === staffId)
  }, [queryData?.absences, showAll, staffId])

  // Legacy function for compatibility
  const fetchAbsences = () => {
    refetch()
  }

  const handleApprove = async (id: string) => {
    try {
      const response = await fetch('/api/hr/absences', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          absenceId: id,
          status: 'approved',
          approvedBy: staffId
        })
      })

      const data = await response.json()
      
      if (data.success) {
        fetchAbsences()
        onApprove?.(id)
      }
    } catch (error) {
      console.error('Approve error:', error)
    }
  }

  const handleReject = async (id: string) => {
    const reason = prompt(t('leave.rejectionReason'))
    if (!reason) return

    try {
      const response = await fetch('/api/hr/absences', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          absenceId: id,
          status: 'rejected',
          approvedBy: staffId,
          denialReason: reason
        })
      })

      const data = await response.json()
      
      if (data.success) {
        fetchAbsences()
        onReject?.(id)
      }
    } catch (error) {
      console.error('Reject error:', error)
    }
  }

  const handleCancel = async (id: string) => {
    if (!confirm(t('leave.cancelConfirm'))) {
      return
    }

    try {
      const response = await fetch('/api/hr/absences', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          absenceId: id,
          status: 'cancelled'
        })
      })

      const data = await response.json()
      
      if (data.success) {
        alert(t('leave.cancelSuccess'))
        fetchAbsences()
      } else {
        alert(t('leave.cancelError'))
      }
    } catch (error) {
      console.error('Cancel error:', error)
      alert(t('leave.cancelError'))
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="border-yellow-500 text-yellow-700">
          <Clock className="h-3 w-3 mr-1" />
          {t('leave.pending')}
        </Badge>
      case 'approved':
        return <Badge variant="default" className="bg-green-600">
          <Check className="h-3 w-3 mr-1" />
          {t('leave.approved')}
        </Badge>
      case 'rejected':
        return <Badge variant="destructive">
          <X className="h-3 w-3 mr-1" />
          {t('leave.rejected')}
        </Badge>
      case 'cancelled':
      case 'withdrawn':
        return <Badge variant="secondary">
          <X className="h-3 w-3 mr-1" />
          {t('leave.cancelled')}
        </Badge>
      default:
        return <Badge variant="secondary">{status}</Badge>
    }
  }

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'vacation': t('leave.vacation'),
      'sick_leave': t('leave.sickLeave'),
      'parental_leave': t('leave.parentalLeave'),
      'unpaid_leave': t('leave.unpaidLeave'),
      'other': t('leave.other')
    }
    return labels[type] || type
  }

  const filteredAbsences = absences.filter(absence => {
    if (filter === 'all') return true
    return absence.status === filter
  })

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t('leave.leaveRequests')}</CardTitle>
          <div className="flex gap-2">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
            >
              {t('leave.all')}
            </Button>
            <Button
              variant={filter === 'pending' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('pending')}
            >
              {t('leave.pending')}
            </Button>
            <Button
              variant={filter === 'approved' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('approved')}
            >
              {t('leave.approved')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {showAll && <TableHead>{t('leave.staff')}</TableHead>}
                <TableHead>{t('leave.type')}</TableHead>
                <TableHead>{t('leave.startDate')}</TableHead>
                <TableHead>{t('leave.endDate')}</TableHead>
                <TableHead>{t('leave.days')}</TableHead>
                <TableHead>{t('timeEntries.status')}</TableHead>
                <TableHead className="text-right">{t('timeEntries.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAbsences.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showAll ? 7 : 6} className="text-center text-muted-foreground">
                    {t('leave.noRequests')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredAbsences.map((absence) => (
                  <TableRow key={absence.id}>
                    {showAll && (
                      <TableCell className="font-medium">
                        {absence.staff?.fullName || 'Unknown'}
                      </TableCell>
                    )}
                    <TableCell>{getTypeLabel(absence.absenceType)}</TableCell>
                    <TableCell>
                      {new Date(absence.startDate).toLocaleDateString('sv-SE')}
                    </TableCell>
                    <TableCell>
                      {new Date(absence.endDate).toLocaleDateString('sv-SE')}
                    </TableCell>
                    <TableCell>{absence.totalDays} {t('leave.days')}</TableCell>
                    <TableCell>{getStatusBadge(absence.status)}</TableCell>
                    <TableCell className="text-right">
                      {absence.status === 'pending' && (
                        showAll ? (
                          // Admin view: Approve/Reject buttons
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="default"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleApprove(absence.id)}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              {t('leave.approve')}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleReject(absence.id)}
                            >
                              <X className="h-4 w-4 mr-1" />
                              {t('leave.reject')}
                            </Button>
                          </div>
                        ) : (
                          // Staff view: Cancel button
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCancel(absence.id)}
                          >
                            <X className="h-4 w-4 mr-1" />
                            {t('leave.cancelRequest')}
                          </Button>
                        )
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

